/**
 * Articles Filters Component
 * Uses abstractions from config for UI components
 */

'use client';

import { Switch } from '../components/ui/switch';
import { FilterSearch } from '../components/filters/filter-search';
import { FilterSummary } from '../components/filters/filter-summary';
import { FilterThemesAutocomplete } from '@uicwebmaster/taxonomy-themes/filters';
import { FilterTags } from '../components/filters/filter-tags';

export interface ArticlesFiltersProps {
  // Search
  query: string;
  onQueryChange: (value: string) => void;

  // Themes
  themes: Array<{
    id: string;
    name: string;
    color?: string | null;
    level?: number;
    children?: Array<{
      id: string;
      name: string;
      color?: string | null;
      level?: number;
      children?: any[];
    }>;
  }>;
  selectedThemes: string[];
  onToggleTheme: (themeId: string) => void;
  onClearThemeSelection: () => void;
  themeFilterMode: 'OR' | 'AND';
  onThemeFilterModeChange: (mode: 'OR' | 'AND') => void;

  // Tags
  tags: Array<{ id: string; name: string }>;
  selectedTags: string[];
  onToggleTag: (tagId: string) => void;
  onClearTagSelection: () => void;
  tagFilterMode: 'OR' | 'AND';
  onTagFilterModeChange: (mode: 'OR' | 'AND') => void;

  // Sticky only
  stickyOnly: boolean;
  onStickyOnlyChange: (stickyOnly: boolean) => void;

  // Results
  totalResults: number;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;

  // Enable/disable specific filters
  enabledFilters?: {
    search?: boolean;
    stickyOnly?: boolean;
    themes?: boolean;
    tags?: boolean;
  };
}

export function ArticlesFilters({
  query,
  onQueryChange,
  themes,
  selectedThemes,
  onToggleTheme,
  onClearThemeSelection,
  themeFilterMode,
  onThemeFilterModeChange,
  tags,
  selectedTags,
  onToggleTag,
  onClearTagSelection,
  tagFilterMode,
  onTagFilterModeChange,
  stickyOnly,
  onStickyOnlyChange,
  totalResults,
  hasActiveFilters,
  onClearAllFilters,
  enabledFilters = { search: true, stickyOnly: true, themes: true, tags: true },
}: ArticlesFiltersProps) {
  return (
    <div className="space-y-4">
      {/* Search */}
      {enabledFilters.search !== false && (
        <FilterSearch query={query} onQueryChange={onQueryChange} placeholder="Search articles..." />
      )}

      {/* Sticky Only Toggle */}
      {enabledFilters.stickyOnly !== false && (
        <div className="rounded-md border p-4">
          <div className="flex items-center justify-between">
            <label htmlFor="sticky-only" className="text-sm font-medium">
              Featured articles only
            </label>
            <Switch
              id="sticky-only"
              checked={stickyOnly}
              onCheckedChange={onStickyOnlyChange}
            />
          </div>
        </div>
      )}

      {/* Themes Filter - Autocomplete */}
      {enabledFilters.themes !== false && themes.length > 0 && (
        <FilterThemesAutocomplete
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={onToggleTheme}
          onClearSelection={onClearThemeSelection}
          placeholder="Select themes..."
        />
      )}

      {/* Tags Filter */}
      {enabledFilters.tags !== false && tags.length > 0 && (
        <div className="rounded-md border p-4">
          <FilterTags
            tags={tags}
            selectedTags={selectedTags}
            onToggleTag={onToggleTag}
            onClearSelection={onClearTagSelection}
            filterMode={tagFilterMode}
            onFilterModeChange={onTagFilterModeChange}
          />
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <FilterSummary
          totalResults={totalResults}
          onClearAll={onClearAllFilters}
          nounSingular="article"
          nounPlural="articles"
        />
      )}
    </div>
  );
}

